/**
 * Question Stats Service
 * 
 * Tracks student performance per question for adaptive practice mode.
 * Stores statistics in Firestore: student_stats/{studentId}/questionStats/{questionId}
 * 
 * Uses unifiedDataService.QuestionStatsService for data access.
 */

import { QuestionStatsService } from '../firebase/unifiedDataService';
import { Logger } from './logger';

const log = Logger.create('QuestionStatsService');

/**
 * Generate unique question ID from question content
 * @param {Object} question - Question object
 * @returns {string} - Unique hash/ID
 */
export function generateQuestionId(question) {
  const content = `${question.question}_${JSON.stringify(question.options)}`;
  // Simple hash function (consider using crypto.subtle.digest for production)
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `q_${Math.abs(hash)}`;
}

/**
 * Record question statistics after student answers
 * @param {string} studentId - Student's user ID
 * @param {Object} question - Question object
 * @param {string} courseId - Course ID
 * @param {string} sourceQuizId - Quiz session ID
 * @param {string} sourceQuizName - Quiz name
 * @param {boolean} isCorrect - Was answer correct?
 * @param {number} timeSpent - Time spent in seconds
 */
export async function recordQuestionStats({
  studentId,
  question,
  courseId,
  sourceQuizId,
  sourceQuizName,
  isCorrect,
  timeSpent
}) {
  if (!studentId || !question) {
    log.warn('recordQuestionStats: Missing required parameters');
    return;
  }

  const questionId = generateQuestionId(question);

  try {
    const existing = await QuestionStatsService.get(studentId, questionId);

    if (existing) {
      // Update existing stats
      const newTimesAnswered = existing.timesAnswered + 1;
      const newAvgTime = 
        (existing.averageTimeSeconds * existing.timesAnswered + timeSpent) / newTimesAnswered;

      await QuestionStatsService.updateWithIncrement(studentId, questionId, {
        timesAnswered: QuestionStatsService.increment(1),
        timesCorrect: isCorrect ? QuestionStatsService.increment(1) : existing.timesCorrect,
        lastAnswered: Date.now(),
        averageTimeSeconds: newAvgTime,
        lastSourceQuizId: sourceQuizId,
        lastSourceQuizName: sourceQuizName
      });
    } else {
      // Create new stats entry
      await QuestionStatsService.upsert(studentId, questionId, {
        questionId,
        questionText: question.question,
        courseId: courseId || null,
        timesAnswered: 1,
        timesCorrect: isCorrect ? 1 : 0,
        lastAnswered: Date.now(),
        averageTimeSeconds: timeSpent,
        firstSeenAt: Date.now(),
        lastSourceQuizId: sourceQuizId,
        lastSourceQuizName: sourceQuizName
      });
    }

    log.success(`Question stats recorded: ${questionId} (correct: ${isCorrect})`);
  } catch (error) {
    log.error('Error recording question stats:', error);
  }
}

/**
 * Get all question stats for a specific course
 * @param {string} studentId - Student's user ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Array>} - Array of question stats
 */
export async function getCourseQuestionStats(studentId, courseId) {
  if (!studentId || !courseId) return [];

  try {
    return await QuestionStatsService.getByCourse(studentId, courseId);
  } catch (error) {
    log.error('Error fetching course question stats:', error);
    return [];
  }
}

/**
 * Get all question stats for a student (all courses)
 * @param {string} studentId - Student's user ID
 * @returns {Promise<Array>} - Array of question stats
 */
export async function getAllQuestionStats(studentId) {
  if (!studentId) return [];

  try {
    return await QuestionStatsService.getAll(studentId);
  } catch (error) {
    log.error('Error fetching all question stats:', error);
    return [];
  }
}

/**
 * Calculate error rate for a question
 * @param {Object} stats - Question stats object
 * @returns {number} - Error rate (0-1)
 */
export function calculateErrorRate(stats) {
  if (!stats || stats.timesAnswered === 0) return 0;
  return 1 - (stats.timesCorrect / stats.timesAnswered);
}

/**
 * Generate practice quiz from question stats
 * @param {Array} questionStats - Array of question stats
 * @param {Object} settings - Practice settings
 * @returns {Array} - Selected questions for practice
 */
export function generatePracticeQuiz(questionStats, settings = {}) {
  const {
    questionCount = 10,
    focusWeakQuestions = false,
    includeNewQuestions = true
  } = settings;

  if (!questionStats || questionStats.length === 0) return [];

  // Add error rate to each question
  const withErrorRate = questionStats.map(q => ({
    ...q,
    errorRate: calculateErrorRate(q)
  }));

  // Sort based on settings
  if (focusWeakQuestions) {
    // Sort by error rate (highest first)
    withErrorRate.sort((a, b) => b.errorRate - a.errorRate);
  } else {
    // Shuffle randomly
    withErrorRate.sort(() => Math.random() - 0.5);
  }

  // Determine count
  const count = questionCount === 'all' 
    ? withErrorRate.length 
    : Math.min(questionCount, withErrorRate.length);

  return withErrorRate.slice(0, count);
}

/**
 * Get practice quiz settings summary
 * @param {string} studentId - Student's user ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} - Stats summary
 */
export async function getPracticeQuizSummary(studentId, courseId) {
  const stats = await getCourseQuestionStats(studentId, courseId);

  return {
    totalQuestions: stats.length,
    averageErrorRate: stats.length > 0
      ? stats.reduce((sum, q) => sum + calculateErrorRate(q), 0) / stats.length
      : 0,
    mostDifficult: stats.sort((a, b) => 
      calculateErrorRate(b) - calculateErrorRate(a)
    ).slice(0, 5),
    recentlyAnswered: stats.sort((a, b) => 
      b.lastAnswered - a.lastAnswered
    ).slice(0, 5)
  };
}

export default {
  generateQuestionId,
  recordQuestionStats,
  getCourseQuestionStats,
  getAllQuestionStats,
  calculateErrorRate,
  generatePracticeQuiz,
  getPracticeQuizSummary
};
